import React from 'react';
import { motion } from 'framer-motion';
import { AnimatedCounter } from '../ui/AnimatedCounter';
export function ProblemSection() {
  const stats = [{
    value: 2,
    suffix: 'x',
    label: 'Startups with cofounders are more than twice as likely to succeed long-term',
    source: 'UNSW & Oxford',
    sourceDetail: 'Scientific Reports',
    year: '2023',
    link: 'https://www.unsw.edu.au/newsroom/news/2023/10/founder-personality-could-predict-start-up-success--study'
  }, {
    value: 8,
    suffix: '-10x',
    label: 'Startups with diverse, complementary founder teams are more likely to succeed',
    source: 'UNSW & Oxford',
    sourceDetail: 'Scientific Reports',
    year: '2023',
    link: 'https://www.nature.com/articles/s41598-023-41980-y'
  }, {
    value: 70,
    suffix: '%',
    label: 'Of differences in employee engagement are explained by management quality',
    source: 'Gallup',
    sourceDetail: 'State of the American Manager',
    year: '2015',
    link: 'https://news.gallup.com/businessjournal/182792/managers-account-variance-employee-engagement.aspx'
  }, {
    value: 55,
    suffix: '%',
    label: 'Of co-founder relationships end within 4 years',
    source: 'Yael Daniely',
    sourceDetail: '400+ Founder Study',
    year: '2019-2021',
    link: 'https://yaeldaniely.com/2023/09/09/unspoken-yet-lethal-dysfunctional-co-founders-relationships/'
  }, {
    value: 33,
    suffix: '%',
    label: 'Of founders cite misaligned vision as the core reason for conflict',
    source: 'WinSavvy',
    sourceDetail: 'Founder Research',
    year: '',
    link: 'https://www.winsavvy.com/founder-conflicts-and-startup-failure-correlation-stats-you-should-know/'
  }, {
    value: 20,
    suffix: '%',
    label: 'Of YC startups have had a co-founder leave',
    source: 'Paul Graham',
    sourceDetail: 'Y Combinator',
    year: '',
    link: 'https://medium.com/getshuffleapp/a-simple-framework-to-assess-your-co-founder-fit-f55aee1a449b'
  }];
  return <section id="problem" className="px-4 sm:px-8 md:px-[60px] py-[140px] bg-white overflow-hidden" data-id="element-1096">
      <div className="max-w-[1400px] mx-auto" data-id="element-1097">
        <div className="grid lg:grid-cols-2 gap-[80px] items-start" data-id="element-1098">
          {/* Left: Problem Statement */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1099">
            <div className="inline-flex items-center px-4 py-2 rounded bg-[#1A3D3A] text-white mb-4" style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '12px',
            fontWeight: 600,
            letterSpacing: '3px',
            lineHeight: 1.0,
            borderRadius: '4px'
          }} data-id="element-1100">
              THE PROBLEM
            </div>
            <h2 className="text-[#1A3D3A] mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 'clamp(36px, 5vw, 52px)',
            fontWeight: 500,
            lineHeight: 1.15,
            letterSpacing: '-1px'
          }} data-id="element-1101">
              65% of startups fail due to cofounder conflict
            </h2>
            <p className="text-[#3D5654] mb-4" style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '18px',
            fontWeight: 400,
            lineHeight: 1.7
          }} data-id="element-1102">
              Harvard Business School professor Noam Wasserman studied 10,000
              founders and found that people problems—not product or market
              issues—are the leading cause of startup failure.
            </p>
            <p className="text-[#3D5654]" style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '14px',
            fontWeight: 400,
            fontStyle: 'italic'
          }} data-id="element-1103">
              Source:{' '}
              <a href="https://www.hbs.edu/faculty/Pages/item.aspx?num=42425" target="_blank" rel="noopener noreferrer" className="text-[#1A3D3A] underline" data-id="element-1104">
                "The Founder's Dilemmas" — Harvard Business School
              </a>
            </p>
          </motion.div>

          {/* Right: Stats Grid */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-1105">
            {stats.map((stat, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1,
            duration: 0.5
          }} className="bg-[#F8FAF9] rounded-[12px] p-8 border border-[rgba(26,61,58,0.15)]" style={{
            boxShadow: '0 8px 40px rgba(26, 61, 58, 0.1)'
          }} data-id="element-1106">
                <div className="mb-3" data-id="element-1107">
                  <AnimatedCounter value={stat.value} suffix={stat.suffix} decimals={stat.suffix === '%' && stat.value === 82.5 ? 1 : 0} className="text-[#1A3D3A] tracking-tight" style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: '48px',
                fontWeight: 600
              }} data-id="element-1108" />
                </div>
                <p className="text-[#3D5654] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: 1.5
            }} data-id="element-1109">
                  {stat.label}
                </p>
                <p className="text-[#3D5654]" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '12px',
              fontWeight: 400,
              fontStyle: 'italic',
              opacity: 0.7
            }} data-id="element-1110">
                  Source:{' '}
                  <a href={stat.link} target="_blank" rel="noopener noreferrer" className="text-[#1A3D3A] underline" data-id="element-1111">
                    {stat.source}
                    {stat.sourceDetail && `, ${stat.sourceDetail}`}, {stat.year}
                  </a>
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </div>
    </section>;
}